import React, { useState, useCallback } from 'react';
import { LabelElement } from '@/types/label';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Eye,
  EyeOff,
  Lock,
  Unlock,
  GripVertical,
  Type,
  QrCode,
  Barcode,
  Image,
  Square,
  Smile,
  Trash2,
  Copy,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface LayersPanelProps {
  elements: LabelElement[];
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onUpdateElement: (id: string, updates: Partial<LabelElement>) => void;
  onDeleteElement: (id: string) => void;
  onDuplicateElement: (id: string) => void;
  onReorderElement: (id: string, direction: 'up' | 'down') => void;
  onMoveElement?: (fromIndex: number, toIndex: number) => void;
}

const getElementIcon = (type: LabelElement['type']) => {
  switch (type) {
    case 'text': return Type;
    case 'qrcode': return QrCode;
    case 'barcode': return Barcode;
    case 'image': return Image;
    case 'shape': return Square;
    case 'icon': return Smile;
    default: return Square;
  }
};

export const LayersPanel: React.FC<LayersPanelProps> = ({
  elements,
  selectedElementId,
  onSelectElement,
  onUpdateElement,
  onDeleteElement,
  onDuplicateElement,
  onReorderElement,
  onMoveElement,
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Reverse elements to show top layer first (higher index = top layer = shown first)
  const sortedElements = [...elements].reverse();

  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIndex(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    // Convert from reversed display order to actual array indices
    const fromActualIndex = elements.length - 1 - draggedIndex;
    const toActualIndex = elements.length - 1 - dropIndex;

    if (onMoveElement) {
      onMoveElement(fromActualIndex, toActualIndex);
    } else {
      // Fallback: use reorder multiple times
      const diff = toActualIndex - fromActualIndex;
      const direction = diff > 0 ? 'up' : 'down';
      const elementId = elements[fromActualIndex].id;
      
      for (let i = 0; i < Math.abs(diff); i++) {
        onReorderElement(elementId, direction);
      }
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, elements, onMoveElement, onReorderElement]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  return (
    <div className="h-full flex flex-col min-w-0">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/50 shrink-0">
        <GripVertical className="w-3 h-3 text-muted-foreground shrink-0" />
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap">Layers</span>
        <span className="text-[10px] text-muted-foreground ml-auto tabular-nums shrink-0">{elements.length}</span>
      </div>
      
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-1.5 space-y-0.5 min-w-[140px]">
          {sortedElements.map((element, index) => {
            const Icon = getElementIcon(element.type);
            const isSelected = selectedElementId === element.id;
            const isDragging = draggedIndex === index;
            const isDragOver = dragOverIndex === index;
            
            return (
              <div
                key={element.id}
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
                className={cn(
                  'flex items-center gap-1 px-2 py-1.5 rounded cursor-grab transition-all group',
                  isSelected 
                    ? 'bg-primary/10 text-foreground' 
                    : 'hover:bg-muted/50 text-muted-foreground hover:text-foreground',
                  isDragging && 'opacity-50 cursor-grabbing',
                  isDragOver && 'ring-2 ring-primary ring-offset-1 ring-offset-background'
                )}
                onClick={() => onSelectElement(element.id)}
              >
                <GripVertical className="w-3 h-3 flex-shrink-0 opacity-40 group-hover:opacity-100 cursor-grab" />
                <Icon className="w-3 h-3 flex-shrink-0" />
                
                <span className={cn(
                  'text-xs flex-1 truncate',
                  element.visible === false && 'opacity-40'
                )}>
                  {element.type === 'text' && 'content' in element
                    ? (element as any).content.substring(0, 12) || 'Text'
                    : element.type.charAt(0).toUpperCase() + element.type.slice(1)}
                </span>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateElement(element.id, { visible: !(element.visible !== false) });
                  }}
                >
                  {element.visible !== false ? (
                    <Eye className="w-2.5 h-2.5" />
                  ) : (
                    <EyeOff className="w-2.5 h-2.5 opacity-40" />
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onUpdateElement(element.id, { locked: !element.locked });
                  }}
                >
                  {element.locked ? (
                    <Lock className="w-2.5 h-2.5 text-warning" />
                  ) : (
                    <Unlock className="w-2.5 h-2.5 opacity-40" />
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDuplicateElement(element.id);
                  }}
                >
                  <Copy className="w-2.5 h-2.5" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-destructive opacity-0 group-hover:opacity-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteElement(element.id);
                  }}
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </Button>
              </div>
            );
          })}

          {elements.length === 0 && (
            <div className="text-center text-xs text-muted-foreground py-8">
              No elements
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
